import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminUser } from "@/lib/roles";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, FileDown, FileUp, Search, Pencil, Trash2, Percent, DollarSign, ArrowRightLeft, History, ChevronUp, ChevronDown, ChevronsUpDown, Image as ImageIcon, Check, X, Barcode as BarcodeIcon, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exporters";
import Pagination from "@/components/Pagination";
import { useResponsivePageSize } from "@/hooks/useResponsivePageSize";
import ProductBarcodes from "@/components/ProductBarcodes";
import { BulkActions, SelectionCheckbox, SelectAllCheckbox } from "@/components/BulkActions";
import { generateProductSku } from "@/lib/productSku";
import { computeProfitMargin } from "@/lib/pricingMargin";

const BLANK = { name: "", sku: "", unit_price: 0, wholesale_price: 0, retail_price: 0, description: "", image_url: "", warehouse_stock: 0, categories: "", vendor_name: "" };

/**
 * FreeCombobox — text input with suggestions dropdown. Accepts any typed value
 * (freehand indexing). Selecting an option calls `onPick(option)` for autofill.
 */
function FreeCombobox({ value, onChange, onPick, options, placeholder, testId, getLabel = (o) => o?.name, getSubLabel = () => "" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const needle = (value || "").toLowerCase().trim();
  const matches = useMemo(() => {
    if (!needle) return options.slice(0, 12);
    // Exact match (case-insensitive) hides the dropdown — user has already picked.
    const exact = options.find(o => (getLabel(o) || "").toLowerCase() === needle);
    if (exact && getLabel(exact).toLowerCase() === needle) return [];
    return options.filter(o => {
      const l = (getLabel(o) || "").toLowerCase();
      const s = (getSubLabel(o) || "").toLowerCase();
      return l.includes(needle) || s.includes(needle);
    }).slice(0, 12);
  }, [needle, options]); // eslint-disable-line

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 bg-white border border-[var(--border)] rounded-md shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-menu`}>
          {matches.map((o, i) => (
            <button
              key={o.id || i}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-soft)] border-b border-[var(--border)] last:border-b-0"
              onClick={() => { onPick ? onPick(o) : onChange(getLabel(o)); setOpen(false); }}
              data-testid={`${testId}-opt-${o.id || i}`}
            >
              <div className="capitalize font-medium">{getLabel(o)}</div>
              {getSubLabel(o) && <div className="text-xs text-gray-500 normal-case">{getSubLabel(o)}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const MarginCell = ({ unitCost, retail, mode }) => {
  const { dollar, percent } = computeProfitMargin(unitCost, retail);
  const positive = dollar >= 0;
  const cls = `tabular-nums font-semibold ${positive ? "text-emerald-600" : "text-red-600"}`;
  if (mode === "percent") return <span className={cls}>{percent.toFixed(1)}%</span>;
  return <span className={cls}>{dollar >= 0 ? "$" : "-$"}{Math.abs(dollar).toFixed(2)}</span>;
};

export default function InventoryPage() {
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const canManage = isAdmin || user?.role === "distributing_partner";
  const [products, setProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [partners, setPartners] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vehicleStock, setVehicleStock] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [skuAutoGenerated, setSkuAutoGenerated] = useState(true);
  const [uploadingImg, setUploadingImg] = useState(false);

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [page, setPage] = useState(0);
  const [marginMode, setMarginMode] = useState(() => localStorage.getItem("margin-mode") || "percent");
  const [sortKey, setSortKey] = useState(() => localStorage.getItem("inv-sort-key") || "name");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("inv-sort-dir") || "asc");
  const PAGE_SIZE = useResponsivePageSize(10, 50);

  // Unified Transfer dialog (Warehouse↔DP, DP↔DP, Promo)
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferProduct, setTransferProduct] = useState(null);
  const [transferMode, setTransferMode] = useState("wh_to_ep"); // wh_to_ep | ep_to_wh | ep_to_ep | promo
  const [transferSourceEp, setTransferSourceEp] = useState("");
  const [transferDestEp, setTransferDestEp] = useState("");
  const [transferQty, setTransferQty] = useState(0);
  const [transferNote, setTransferNote] = useState("");
  const [promoRecipientKind, setPromoRecipientKind] = useState("ep"); // ep | client
  const [promoClientId, setPromoClientId] = useState("");
  const [promoClientQuery, setPromoClientQuery] = useState("");
  const [clients, setClients] = useState([]);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerProduct, setLedgerProduct] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);

  // Bulk product image upload
  const [bulkImgOpen, setBulkImgOpen] = useState(false);
  const [bulkImgFiles, setBulkImgFiles] = useState([]);
  const [bulkImgMatches, setBulkImgMatches] = useState([]);
  const [bulkImgBusy, setBulkImgBusy] = useState(false);
  // Per-file manual override — { [filename]: { id, name } | null }
  const [bulkImgAssign, setBulkImgAssign] = useState({});
  // Per-file typed search text — { [filename]: string }
  const [bulkImgSearch, setBulkImgSearch] = useState({});

  // Regenerate SKUs
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenRows, setRegenRows] = useState([]);
  const [regenTotal, setRegenTotal] = useState(0);
  const [regenBusy, setRegenBusy] = useState(false);

  useEffect(() => { localStorage.setItem("margin-mode", marginMode); }, [marginMode]);
  useEffect(() => { localStorage.setItem("inv-sort-key", sortKey); localStorage.setItem("inv-sort-dir", sortDir); }, [sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortHeader = ({ label, keyName, align = "left", extra = "" }) => {
    const active = sortKey === keyName;
    const Icon = !active ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
    return (
      <th className={`text-${align} p-3 cursor-pointer select-none hover:bg-gray-100 transition-colors ${extra}`} onClick={() => toggleSort(keyName)} data-testid={`sort-${keyName}`}>
        <span className={`inline-flex items-center gap-1 ${align === "right" ? "float-right" : ""} ${active ? "text-[var(--brand)]" : ""}`}>
          {label}
          <Icon size={12} className={active ? "opacity-100" : "opacity-40"} />
        </span>
      </th>
    );
  };

  const load = async () => {
    const [p, pp, v, vd, cl] = await Promise.all([
      api.get("/products").then(r => r.data),
      api.get("/endpoint-partners").then(r => r.data).catch(() => []),
      api.get("/inventory/vehicles").then(r => r.data).catch(() => []),
      api.get("/vendors").then(r => r.data).catch(() => []),
      api.get("/clients").then(r => r.data).catch(() => []),
    ]);
    setProducts(p); setPartners(pp.filter(x => x.id !== "warehouse")); setVehicleStock(v); setVendors(vd); setClients(cl);
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const s = new Set();
    products.forEach((p) => { if (p.categories) p.categories.split(",").forEach((c) => s.add(c.trim())); });
    return Array.from(s).filter(Boolean).sort();
  }, [products]);

  const filtered = useMemo(() => {
    const needle = q.toLowerCase().trim();
    return products.filter((p) => {
      if (cat && !(p.categories || "").split(",").map((s) => s.trim()).includes(cat)) return false;
      if (!needle) return true;
      return (p.name?.toLowerCase().includes(needle)
              || p.sku?.toLowerCase().includes(needle)
              || p.vendor_name?.toLowerCase().includes(needle)
              || p.categories?.toLowerCase().includes(needle));
    });
  }, [products, q, cat]);

  useEffect(() => { setPage(0); }, [q, cat]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    const getVal = (p) => {
      switch (sortKey) {
        case "sku": return (p.sku || "").toLowerCase();
        case "vendor": return (p.vendor_name || "").toLowerCase();
        case "wholesale": return Number(p.wholesale_price) || 0;
        case "retail": return Number(p.retail_price) || 0;
        case "margin": {
          const { dollar, percent } = computeProfitMargin(p.unit_price, p.retail_price);
          return marginMode === "percent" ? percent : dollar;
        }
        case "stock": return Number(p.warehouse_stock) || 0;
        case "name":
        default: return (p.name || "").toLowerCase();
      }
    };
    rows.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return rows;
  }, [filtered, sortKey, sortDir, marginMode]);

  const paged = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));

  const openAdd = () => { setForm(BLANK); setSkuAutoGenerated(true); setAddOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", sku: p.sku || "",
      unit_price: p.unit_price ?? 0,
      wholesale_price: p.wholesale_price ?? 0,
      retail_price: p.retail_price ?? 0,
      description: p.description || "",
      image_url: p.image_url || "",
      warehouse_stock: p.warehouse_stock ?? 0,
      categories: p.categories || "",
      vendor_name: p.vendor_name || "",
    });
    setSkuAutoGenerated(false);
    setEditOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sku.trim()) { toast.error("SKU is required"); return; }
    const payload = { ...form,
      unit_price: Number(form.unit_price) || 0,
      wholesale_price: Number(form.wholesale_price) || 0,
      retail_price: Number(form.retail_price) || 0,
      warehouse_stock: Number(form.warehouse_stock) || 0,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated"); setEditOpen(false); setEditing(null);
      } else {
        await api.post("/products", payload);
        toast.success("Product added"); setAddOpen(false);
      }
      setForm(BLANK); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" (${p.sku})? This cannot be undone.`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Product deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Admin-only: flip a product's public visibility. Optimistic update so the icon
  // toggles instantly; server errors roll back and surface a toast.
  const toggleVisibility = async (p) => {
    const next = p.is_visible === false ? true : false;
    setProducts((cur) => cur.map((x) => (x.id === p.id ? { ...x, is_visible: next } : x)));
    try {
      await api.patch(`/products/${p.id}/visibility`, { is_visible: next });
      toast.success(next ? "Product shown on storefront" : "Product hidden from storefront");
    } catch (err) {
      setProducts((cur) => cur.map((x) => (x.id === p.id ? { ...x, is_visible: p.is_visible } : x)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Toggle failed");
    }
  };

  const updateStock = async (personnelId, productId, qty) => {
    try {
      await api.post(`/inventory/vehicles/${personnelId}`, { product_id: productId, qty: Number(qty) });
      toast.success("Stock updated"); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // Upload a new image for the currently-being-edited product straight into
  // Emergent Object Store. Only works when editing an existing product (we
  // need a product_id to key the storage path).
  const uploadProductImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !editing?.id) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/products/${editing.id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingImg(false);
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/products/import.csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} products`); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    e.target.value = "";
  };

  // Bulk product image upload: choose many files, preview matches, then commit.
  const openBulkImages = () => {
    setBulkImgFiles([]); setBulkImgMatches([]); setBulkImgAssign({}); setBulkImgSearch({}); setBulkImgOpen(true);
  };
  const onBulkImgPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setBulkImgFiles(files);
    setBulkImgBusy(true);
    // Seed empty overrides
    setBulkImgAssign(Object.fromEntries(files.map(f => [f.name, null])));
    setBulkImgSearch(Object.fromEntries(files.map(f => [f.name, ""])));
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const { data } = await api.post("/inventory/bulk-images/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBulkImgMatches(data.matches || []);
      // Pre-fill overrides + search text with auto-match suggestions
      const seed = {}, srch = {};
      (data.matches || []).forEach(m => {
        if (m.matched_product_id) {
          seed[m.filename] = { id: m.matched_product_id, name: m.matched_name, sku: m.matched_sku };
          srch[m.filename] = m.matched_name || "";
        } else {
          seed[m.filename] = null;
          srch[m.filename] = "";
        }
      });
      setBulkImgAssign(seed);
      setBulkImgSearch(srch);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Preview failed");
    } finally { setBulkImgBusy(false); }
  };
  const submitBulkImages = async () => {
    if (bulkImgFiles.length === 0) return;
    // Build assignments from manual picks (only files with a picked product)
    const assignments = Object.entries(bulkImgAssign)
      .filter(([_, v]) => v && v.id)
      .map(([filename, v]) => ({ filename, product_id: v.id }));
    if (assignments.length === 0) {
      toast.error("Pick a product for at least one image before saving.");
      return;
    }
    setBulkImgBusy(true);
    try {
      const fd = new FormData();
      bulkImgFiles.forEach(f => fd.append("files", f));
      fd.append("assignments", JSON.stringify(assignments));
      const { data } = await api.post("/inventory/bulk-images/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Attached ${data.updated} image${data.updated === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setBulkImgOpen(false); setBulkImgFiles([]); setBulkImgMatches([]); setBulkImgAssign({}); setBulkImgSearch({});
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setBulkImgBusy(false); }
  };

  const openRegenSkus = async () => {
    setRegenOpen(true);
    setRegenBusy(true);
    setRegenRows([]);
    try {
      const { data } = await api.post("/products/regenerate-skus/preview");
      setRegenRows(data.rows || []);
      setRegenTotal(data.total || 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Preview failed");
    } finally { setRegenBusy(false); }
  };
  const submitRegenSkus = async () => {
    if (!window.confirm(`Regenerate SKUs for all ${regenTotal} products? Existing SKUs will be replaced.`)) return;
    setRegenBusy(true);
    try {
      const { data } = await api.post("/products/regenerate-skus");
      toast.success(`Regenerated ${data.updated} SKUs`);
      setRegenOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Regenerate failed");
    } finally { setRegenBusy(false); }
  };

  const openTransfer = (p) => {
    setTransferProduct(p);
    setTransferMode("wh_to_ep");
    setTransferSourceEp(partners[0]?.id || "");
    setTransferDestEp(partners[0]?.id || "");
    setTransferQty(0); setTransferNote("");
    setPromoRecipientKind("ep"); setPromoClientId(""); setPromoClientQuery("");
    setTransferOpen(true);
  };

  // On-hand snapshot for the currently-picked source
  const sourceOnHand = useMemo(() => {
    if (!transferProduct) return 0;
    if (transferMode === "wh_to_ep" || (transferMode === "promo" && !transferSourceEp)) {
      return Number(transferProduct.warehouse_stock) || 0;
    }
    const epId = transferMode === "promo" ? transferSourceEp : transferSourceEp;
    const row = vehicleStock.find(v => v.personnel_id === epId && v.product_id === transferProduct.id);
    return Number(row?.qty) || 0;
  }, [transferProduct, transferMode, transferSourceEp, vehicleStock]);

  const submitTransfer = async () => {
    if (!transferProduct) return;
    const qty = Number(transferQty);
    if (qty <= 0) { toast.error("Qty must be > 0"); return; }
    try {
      let msg = "";
      if (transferMode === "wh_to_ep") {
        if (!transferDestEp) return toast.error("Pick a destination DP");
        const { data } = await api.post("/transfers/warehouse-to-ep", {
          ep_id: transferDestEp, product_id: transferProduct.id, qty, note: transferNote,
        });
        msg = `Warehouse → DP · ${qty}. Warehouse now ${data.product.warehouse_stock}.`;
      } else if (transferMode === "ep_to_wh") {
        if (!transferSourceEp) return toast.error("Pick a source DP");
        const { data } = await api.post("/transfers/ep-to-warehouse", {
          ep_id: transferSourceEp, product_id: transferProduct.id, qty, note: transferNote,
        });
        msg = `DP → Warehouse · ${qty}. Warehouse now ${data.product.warehouse_stock}.`;
      } else if (transferMode === "ep_to_ep") {
        if (!transferSourceEp || !transferDestEp) return toast.error("Pick source and destination DPs");
        if (transferSourceEp === transferDestEp) return toast.error("Source and destination must differ");
        await api.post("/transfers/ep-to-ep", {
          from_ep_id: transferSourceEp, to_ep_id: transferDestEp,
          product_id: transferProduct.id, qty, note: transferNote,
        });
        msg = `DP → DP · ${qty} moved.`;
      } else if (transferMode === "promo") {
        const payload = {
          product_id: transferProduct.id, qty,
          source_kind: transferSourceEp ? "ep" : "warehouse",
          source_ep_id: transferSourceEp || null,
          recipient_kind: promoRecipientKind,
          recipient_ep_id: promoRecipientKind === "ep" ? transferDestEp : null,
          recipient_client_id: promoRecipientKind === "client" ? promoClientId : null,
          reason: transferNote,
        };
        if (promoRecipientKind === "ep" && !transferDestEp) return toast.error("Pick a recipient DP");
        if (promoRecipientKind === "client" && !promoClientId) return toast.error("Pick a recipient client");
        await api.post("/transfers/promo", payload);
        msg = `Promo gifted · ${qty} unit${qty === 1 ? "" : "s"}`;
      }
      toast.success(msg);
      setTransferOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openLedger = async (p) => {
    setLedgerProduct(p); setLedgerRows([]); setLedgerOpen(true);
    try {
      const { data } = await api.get(`/stock-movements?product_id=${p.id}&limit=100`);
      setLedgerRows(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const setAutoSkuForm = (changes) => {
    setForm((current) => {
      const next = { ...current, ...changes };
      if (!editing && skuAutoGenerated && next.name.trim()) next.sku = generateProductSku(next.name, next.vendor_name);
      return next;
    });
  };

  const ProductForm = (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-form">
      <div className="col-span-2">
        <Label>Name *</Label>
        <FreeCombobox
          value={form.name}
          onChange={(v) => setAutoSkuForm({ name: v })}
          onPick={(p) => {
            // Autofill from picked existing product (only if fields are empty / adding new)
            setForm((f) => {
              const next = {
                ...f,
              name: p.name || "",
              vendor_name: editing ? f.vendor_name : (p.vendor_name || f.vendor_name),
              wholesale_price: editing ? f.wholesale_price : (p.wholesale_price ?? f.wholesale_price),
              retail_price: editing ? f.retail_price : (p.retail_price ?? f.retail_price),
              categories: editing ? f.categories : (p.categories || f.categories),
              image_url: editing ? f.image_url : (p.image_url || f.image_url),
              description: editing ? f.description : (p.description || f.description),
              };
              if (!editing && skuAutoGenerated) next.sku = generateProductSku(next.name, next.vendor_name);
              return next;
            });
          }}
          options={products}
          placeholder="Type a new name or pick an existing product…"
          testId="product-name"
          getLabel={(o) => o?.name}
          getSubLabel={(o) => o?.sku ? `${o.sku}${o.vendor_name ? " · " + o.vendor_name : ""}` : ""}
        />
      </div>
      <div><Label>SKU *</Label><Input required value={form.sku} onChange={e => { setSkuAutoGenerated(false); setForm({ ...form, sku: e.target.value }); }} data-testid="product-sku" /></div>
      <div>
        <Label>Vendor</Label>
        <FreeCombobox
          value={form.vendor_name}
          onChange={(v) => setAutoSkuForm({ vendor_name: v })}
          onPick={(v) => setAutoSkuForm({ vendor_name: v.company || v.name || "" })}
          options={vendors}
          placeholder="Type or pick a vendor…"
          testId="product-vendor"
          getLabel={(o) => o?.company || o?.name}
          getSubLabel={(o) => o?.poc_name || o?.poc_email || ""}
        />
      </div>

      {/* Pricing matrix panel */}
      <div className="col-span-2 border border-[var(--border)] rounded-lg p-3 bg-[var(--bg-soft)]" data-testid="pricing-matrix-form">
        <div className="flex items-center justify-between mb-2">
          <p className="label-eyebrow">Pricing matrix</p>
          <div className="inline-flex text-xs bg-white border border-[var(--border)] rounded-md overflow-hidden">
            <button type="button" onClick={() => setMarginMode("percent")}
                    className={`px-2 py-1 flex items-center gap-1 ${marginMode === "percent" ? "bg-[var(--brand)] text-white" : "text-gray-600"}`}
                    data-testid="form-margin-percent">
              <Percent size={11} /> %
            </button>
            <button type="button" onClick={() => setMarginMode("dollar")}
                    className={`px-2 py-1 flex items-center gap-1 ${marginMode === "dollar" ? "bg-[var(--brand)] text-white" : "text-gray-600"}`}
                    data-testid="form-margin-dollar">
              <DollarSign size={11} /> $
            </button>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <div>
            <Label className="text-xs">Unit ($)</Label>
            <Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} data-testid="product-unit" />
          </div>
          <div>
            <Label className="text-xs">Wholesale ($)</Label>
            <Input type="number" step="0.01" value={form.wholesale_price} onChange={e => setForm({ ...form, wholesale_price: e.target.value })} data-testid="product-price" />
          </div>
          <div>
            <Label className="text-xs">Retail ($)</Label>
            <Input type="number" step="0.01" value={form.retail_price} onChange={e => setForm({ ...form, retail_price: e.target.value })} data-testid="product-retail" />
          </div>
          <div>
            <Label className="text-xs">Profit margin</Label>
            <div className="border border-[var(--border)] rounded-md px-3 py-2 bg-white text-sm h-10 flex items-center" data-testid="form-margin-display">
              <MarginCell unitCost={form.unit_price} retail={form.retail_price} mode={marginMode} />
            </div>
          </div>
        </div>
        {/* Quick markup buttons */}
        <div className="flex gap-2 mt-2 items-center">
          <span className="text-xs text-gray-500">Quick markup:</span>
          {[1.5, 2, 2.5, 3].map((m) => (
            <button key={m} type="button" onClick={() => setForm({ ...form, retail_price: (Number(form.wholesale_price) * m).toFixed(2) })}
                    className="text-xs px-2 py-1 border border-[var(--border)] rounded hover:bg-white" data-testid={`markup-${m}x`}>
              {m}×
            </button>
          ))}
        </div>
      </div>

      <div><Label>Warehouse stock</Label><Input type="number" value={form.warehouse_stock} onChange={e => setForm({ ...form, warehouse_stock: e.target.value })} data-testid="product-stock" /></div>
      <div><Label>Categories</Label><Input value={form.categories} onChange={e => setForm({ ...form, categories: e.target.value })} placeholder="Hair, Tools" data-testid="product-categories" /></div>
      <div className="col-span-2">
        <Label>Image URL</Label>
        <div className="flex gap-2 items-start">
          <Input
            className="flex-1"
            value={form.image_url}
            onChange={e => setForm({ ...form, image_url: e.target.value })}
            placeholder="/api/product-images/… or paste any URL"
            data-testid="product-image-url"
          />
          {editing?.id ? (
            <label className="inline-flex shrink-0">
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={uploadProductImage}
                data-testid="product-image-upload-input"
              />
              <span className={`inline-flex items-center cursor-pointer px-3 py-2 border border-[var(--border)] rounded-md text-sm font-medium hover:bg-[var(--bg-soft)] ${uploadingImg ? "opacity-60 pointer-events-none" : ""}`} data-testid="product-image-upload-button">
                <FileUp size={14} className="mr-1" /> {uploadingImg ? "Uploading…" : "Upload new image"}
              </span>
            </label>
          ) : (
            <span className="text-[10px] text-gray-400 self-center px-2" title="Save the product first to enable image upload">Save first to upload</span>
          )}
        </div>
        {form.image_url && (
          <div className="mt-2 flex items-center gap-3">
            <img src={form.image_url} alt="preview" className="w-16 h-16 rounded border border-[var(--border)] object-cover bg-[var(--bg-soft)]" data-testid="product-image-preview" />
            <span className="text-[10px] text-gray-500 font-mono break-all">{form.image_url}</span>
          </div>
        )}
      </div>
      <div className="col-span-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
      <Button className="btn-brand col-span-2" data-testid="submit-product">{editing ? "Save changes" : "Add product"}</Button>
    </form>
  );

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="flex justify-between items-end mb-3 sm:mb-6 flex-wrap gap-2 sm:gap-3">
        <div className="min-w-0">
          <p className="label-eyebrow">Stock & supply</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-1">Inventory</h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">{products.length} products · {filtered.length} showing</p>
        </div>
        {canManage && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => downloadCsv("/products/export.csv", "products.csv")} data-testid="export-products-csv">
              <FileDown size={14} className="mr-1" /> Export CSV
            </Button>
            <label className="inline-flex">
              <input type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="import-products-csv" />
              <span className="inline-flex items-center cursor-pointer px-4 py-2 border border-[var(--border)] rounded-md text-sm font-medium hover:bg-[var(--bg-soft)]"><FileUp size={14} className="mr-1" /> Import CSV</span>
            </label>
            <Button variant="outline" onClick={openBulkImages} data-testid="bulk-images-button">
              <ImageIcon size={14} className="mr-1" /> Bulk images
            </Button>
            <Button variant="outline" onClick={openRegenSkus} data-testid="regen-skus-button">
              <BarcodeIcon size={14} className="mr-1" /> Regenerate SKUs
            </Button>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild><Button className="btn-brand" onClick={openAdd} data-testid="add-product-button"><Plus size={14} className="mr-1" /> Product</Button></DialogTrigger>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Onboard product</DialogTitle></DialogHeader>
                {ProductForm}
              </DialogContent>
            </Dialog>
          </div>
        )}
      </header>

      <Tabs defaultValue="warehouse">
        <TabsList>
          <TabsTrigger value="warehouse" data-testid="tab-warehouse">Warehouse</TabsTrigger>
          <TabsTrigger value="partners" data-testid="tab-partners">Distributing Partners</TabsTrigger>
        </TabsList>
        <TabsContent value="warehouse">
          <div className="flex flex-wrap gap-3 mt-4 mb-3 items-center">
            <div className="relative flex-1 min-w-64">
              <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
              <Input className="pl-8" placeholder="Search name, SKU, vendor…" value={q} onChange={e => setQ(e.target.value)} data-testid="inventory-search" />
            </div>
            <select className="border border-[var(--border)] rounded-md px-3 h-10 text-sm bg-white" value={cat} onChange={e => setCat(e.target.value)} data-testid="inventory-category-filter">
              <option value="">All categories ({categories.length})</option>
              {categories.map((c) => <option key={String(c)} value={String(c)}>{String(c)}</option>)}
            </select>
            {/* Margin toggle for the table */}
            <div className="inline-flex text-xs bg-white border border-[var(--border)] rounded-md overflow-hidden h-10">
              <button onClick={() => setMarginMode("percent")}
                      className={`px-3 flex items-center gap-1 ${marginMode === "percent" ? "bg-[var(--brand)] text-white" : "text-gray-600"}`}
                      data-testid="margin-toggle-percent">
                <Percent size={12} /> %
              </button>
              <button onClick={() => setMarginMode("dollar")}
                      className={`px-3 flex items-center gap-1 ${marginMode === "dollar" ? "bg-[var(--brand)] text-white" : "text-gray-600"}`}
                      data-testid="margin-toggle-dollar">
                <DollarSign size={12} /> $
              </button>
            </div>
            {(q || cat) && <Button variant="ghost" size="sm" onClick={() => { setQ(""); setCat(""); }} data-testid="clear-filters">Clear</Button>}
          </div>

          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-soft)] text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="p-3 w-10"><SelectAllCheckbox records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} /></th>
                    <SortHeader label="Product" keyName="name" extra="sticky left-0 bg-[var(--bg-soft)] z-20 min-w-[130px]" />
                    <SortHeader label="SKU" keyName="sku" extra="hidden md:table-cell sticky left-[130px] bg-[var(--bg-soft)] z-20 min-w-[110px]" />
                    <SortHeader label="Vendor" keyName="vendor" extra="sticky left-[130px] md:left-[240px] bg-[var(--bg-soft)] z-20 min-w-[100px] border-r border-[var(--border)]" />
                    <SortHeader label="Wholesale" keyName="wholesale" align="right" />
                    <SortHeader label="Retail" keyName="retail" align="right" />
                    <SortHeader label={`Margin (${marginMode === "percent" ? "%" : "$"})`} keyName="margin" align="right" />
                    <SortHeader label="Stock" keyName="stock" align="right" />
                    {canManage && <th className="text-right p-3">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p) => (
                    <tr key={p.id} className="border-t border-[var(--border)] hover:bg-gray-50 group" data-testid={`product-row-${p.id}`}>
                      <td className="p-3"><SelectionCheckbox checked={selectedIds.includes(p.id)} onChange={(checked) => setSelectedIds(checked ? [...selectedIds, p.id] : selectedIds.filter((id) => id !== p.id))} testId={`select-row-checkbox-${p.id}`} label={`Select ${p.name}`} /></td>
                      <td className="p-3 font-medium capitalize sticky left-0 bg-white group-hover:bg-gray-50 z-10 min-w-[130px]">{p.name}</td>
                      <td className="p-3 text-gray-500 font-mono text-xs hidden md:table-cell sticky left-[130px] bg-white group-hover:bg-gray-50 z-10 min-w-[110px]">
                        <div>{p.sku}</div>
                        <ProductBarcodes sku={p.sku} />
                      </td>
                      <td className="p-3 text-gray-600 capitalize sticky left-[130px] md:left-[240px] bg-white group-hover:bg-gray-50 z-10 min-w-[100px] border-r border-[var(--border)]">{p.vendor_name || "—"}</td>
                      <td className="p-3 text-right tabular-nums">${Number(p.wholesale_price || 0).toFixed(2)}</td>
                      <td className="p-3 text-right tabular-nums text-gray-600">${Number(p.retail_price || 0).toFixed(2)}</td>
                      <td className="p-3 text-right" data-testid={`margin-${p.id}`}>
                        <MarginCell unitCost={p.unit_price} retail={p.retail_price} mode={marginMode} />
                      </td>
                      <td className={`p-3 text-right tabular-nums ${(p.warehouse_stock || 0) < 50 ? "text-red-600 font-semibold" : ""}`}>
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => toggleVisibility(p)}
                              title={p.is_visible === false ? "Hidden from storefront — click to show" : "Visible on storefront — click to hide"}
                              className={`inline-flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
                                p.is_visible === false
                                  ? "text-gray-400 hover:bg-red-50 hover:text-red-600"
                                  : "text-emerald-600 hover:bg-emerald-50"
                              }`}
                              data-testid={`visibility-toggle-${p.id}`}
                              aria-label={p.is_visible === false ? "Show product on storefront" : "Hide product from storefront"}
                            >
                              {p.is_visible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                          )}
                          <span>{p.warehouse_stock ?? 0}</span>
                        </div>
                      </td>
                      {canManage && (
                        <td className="p-3 text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => openLedger(p)} title="Stock ledger" data-testid={`ledger-product-${p.id}`}><History size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => openTransfer(p)} title="Transfer to DP" data-testid={`transfer-product-${p.id}`}><ArrowRightLeft size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil size={14} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => remove(p)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-product-${p.id}`}><Trash2 size={14} /></Button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {paged.length === 0 && (
                    <tr><td colSpan={canManage ? 8 : 7} className="p-10 text-center text-gray-400">No products match.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={sorted.length} pageSize={PAGE_SIZE} testid="inventory-pagination" />
          </Card>
        </TabsContent>

        <TabsContent value="partners">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {partners.map((p) => (
              <Card key={p.id} className="p-5" data-testid={`partner-stock-card-${p.id}`}>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold flex items-center gap-2 capitalize"><span className="w-3 h-3 rounded-full" style={{ background: p.color }} />{p.name}</h3>
                  <span className="text-xs text-gray-500">{p.company || p.email}</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-gray-500 uppercase tracking-wide"><tr><th className="text-left">Product</th><th className="text-right">Qty</th></tr></thead>
                  <tbody>
                    {vehicleStock.filter(v => v.personnel_id === p.id && Number(v.qty) > 0).map(s => {
                      const prod = products.find(product => product.id === s.product_id);
                      if (!prod) return null;
                      return (
                        <tr key={prod.id} className="border-t border-[var(--border)]">
                          <td className="py-2 capitalize">{prod.name}</td>
                          <td className="py-2">
                            <Input className="w-24 ml-auto" type="number" defaultValue={s.qty}
                                   onBlur={(e) => updateStock(p.id, prod.id, e.target.value)}
                                   disabled={!isAdmin}
                                   data-testid={`partner-stock-${p.id}-${prod.id}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Card>
            ))}
            {partners.length === 0 && <p className="text-gray-400">No distributing partners yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit modal */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit product · <span className="font-mono text-sm text-gray-500">{editing?.sku}</span></DialogTitle></DialogHeader>
          {ProductForm}
        </DialogContent>
      </Dialog>

      {/* Unified Transfer dialog: Warehouse↔DP · DP↔DP · Promo */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ArrowRightLeft size={16} /> Move / Gift stock</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm" data-testid="transfer-dialog">
            <p className="text-gray-600 capitalize">
              <strong>{transferProduct?.name}</strong>
              <span className="normal-case text-gray-500 ml-2">· SKU {transferProduct?.sku}</span>
            </p>

            {/* Mode picker */}
            <div className="grid grid-cols-4 gap-1 bg-[var(--bg-soft)] p-1 rounded-lg" data-testid="transfer-mode-picker">
              {[
                { k: "wh_to_ep", l: "WH → DP" },
                { k: "ep_to_wh", l: "DP → WH" },
                { k: "ep_to_ep", l: "DP → DP" },
                { k: "promo",    l: "Promo 🎁" },
              ].map(m => (
                <button
                  key={m.k} type="button"
                  onClick={() => { setTransferMode(m.k); setTransferSourceEp(m.k === "wh_to_ep" ? "" : (partners[0]?.id || "")); setTransferDestEp(m.k === "ep_to_wh" ? "" : (partners[0]?.id || "")); }}
                  className={`px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${transferMode === m.k ? "bg-white shadow-sm text-[var(--brand)]" : "text-gray-500 hover:bg-white/60"}`}
                  data-testid={`transfer-mode-${m.k}`}
                >{m.l}</button>
              ))}
            </div>

            {/* Source (DP) — visible for ep_to_wh, ep_to_ep, and promo */}
            {(transferMode === "ep_to_wh" || transferMode === "ep_to_ep" || transferMode === "promo") && (
              <div>
                <Label>Source {transferMode === "promo" ? <span className="text-gray-400 text-xs">(leave blank for Warehouse)</span> : "Distributing Partner"}</Label>
                <select
                  className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white capitalize"
                  value={transferSourceEp} onChange={e => setTransferSourceEp(e.target.value)}
                  data-testid="transfer-source-ep"
                >
                  {transferMode === "promo" && <option value="">Warehouse</option>}
                  {partners.map(p => <option key={p.id} value={p.id}>{p.name}{p.company ? ` — ${p.company}` : ""}</option>)}
                </select>
              </div>
            )}

            {/* Destination */}
            {(transferMode === "wh_to_ep" || transferMode === "ep_to_ep" || (transferMode === "promo" && promoRecipientKind === "ep")) && (
              <div>
                <Label>{transferMode === "wh_to_ep" ? "Destination DP" : transferMode === "ep_to_ep" ? "Destination DP" : "Recipient DP"}</Label>
                <select
                  className="w-full h-10 border border-[var(--border)] rounded-md px-3 bg-white capitalize"
                  value={transferDestEp} onChange={e => setTransferDestEp(e.target.value)}
                  data-testid="transfer-dest-ep"
                >
                  {partners.map(p => <option key={p.id} value={p.id}>{p.name}{p.company ? ` — ${p.company}` : ""}</option>)}
                </select>
              </div>
            )}

            {/* Promo recipient toggle */}
            {transferMode === "promo" && (
              <div>
                <Label>Gift to</Label>
                <div className="grid grid-cols-2 gap-1 bg-[var(--bg-soft)] p-1 rounded-lg">
                  <button type="button" onClick={() => setPromoRecipientKind("ep")}
                          className={`px-2 py-1.5 rounded-md text-xs font-medium ${promoRecipientKind === "ep" ? "bg-white shadow-sm text-[var(--brand)]" : "text-gray-500"}`}
                          data-testid="promo-recipient-ep">Distributing Partner</button>
                  <button type="button" onClick={() => setPromoRecipientKind("client")}
                          className={`px-2 py-1.5 rounded-md text-xs font-medium ${promoRecipientKind === "client" ? "bg-white shadow-sm text-[var(--brand)]" : "text-gray-500"}`}
                          data-testid="promo-recipient-client">Client</button>
                </div>
                {promoRecipientKind === "client" && (
                  <div className="mt-2">
                    <FreeCombobox
                      value={promoClientQuery}
                      onChange={(v) => {
                        setPromoClientQuery(v);
                        // Clear id unless the typed text exactly matches a client name
                        const match = clients.find(c => (c.name || "").toLowerCase() === v.toLowerCase());
                        setPromoClientId(match ? match.id : "");
                      }}
                      onPick={(c) => { setPromoClientId(c.id); setPromoClientQuery(c.name || ""); }}
                      options={clients}
                      placeholder="Type or pick a client…"
                      testId="promo-client-picker"
                      getLabel={(o) => o?.name || "—"}
                      getSubLabel={(o) => o?.company || o?.email || ""}
                    />
                    {promoClientId && <p className="text-[10px] text-emerald-600 mt-1">✓ Client selected</p>}
                  </div>
                )}
              </div>
            )}

            {/* On-hand hint */}
            <div className="text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
              Source on-hand: <strong className="tabular-nums">{sourceOnHand}</strong>
            </div>

            <div>
              <Label>Qty</Label>
              <Input type="number" min="1" max={sourceOnHand || undefined}
                     value={transferQty} onChange={e => setTransferQty(e.target.value)}
                     data-testid="transfer-qty" />
            </div>

            <div>
              <Label>{transferMode === "promo" ? "Reason / campaign" : "Note"}</Label>
              <Input value={transferNote} onChange={e => setTransferNote(e.target.value)}
                     placeholder={transferMode === "promo" ? "e.g. Launch swag, VIP thank-you" : "Optional"}
                     data-testid="transfer-note" />
            </div>

            <Button className="btn-brand w-full" onClick={submitTransfer}
                    disabled={Number(transferQty) <= 0 || Number(transferQty) > sourceOnHand}
                    data-testid="submit-transfer">
              <ArrowRightLeft size={14} className="mr-1" /> {transferMode === "promo" ? "Gift now" : "Move stock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock ledger */}
      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">Stock ledger · {ledgerProduct?.name}</DialogTitle>
          </DialogHeader>
          <div className="text-sm">
            <p className="text-xs text-gray-500 mb-3">SKU {ledgerProduct?.sku} · Current warehouse stock: <strong>{ledgerProduct?.warehouse_stock ?? 0}</strong></p>
            {ledgerRows.length === 0 ? (
              <p className="text-center text-gray-400 py-8">No movements recorded yet. Stock changes made from this point forward will appear here.</p>
            ) : (
              <table className="w-full text-xs" data-testid="ledger-table">
                <thead className="bg-[var(--bg-soft)] text-gray-500">
                  <tr>
                    <th className="text-left p-2">When</th>
                    <th className="text-left p-2">Type</th>
                    <th className="text-left p-2">From</th>
                    <th className="text-left p-2">To</th>
                    <th className="text-right p-2">Qty</th>
                    <th className="text-left p-2">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRows.map(r => (
                    <tr key={r.id} className="border-t border-[var(--border)]">
                      <td className="p-2 whitespace-nowrap">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                      <td className="p-2"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">{r.type}</span></td>
                      <td className="p-2">{r.source}</td>
                      <td className="p-2">{r.destination}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">{r.qty >= 0 ? "+" : ""}{r.qty}</td>
                      <td className="p-2 text-gray-500">{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk product image upload */}
      <Dialog open={bulkImgOpen} onOpenChange={setBulkImgOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="bulk-images-dialog">
          <DialogHeader>
            <DialogTitle>Bulk product images</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-4">
            <div className="text-xs text-gray-500 leading-relaxed">
              Pick image files, then for each one confirm or pick the right product from the dropdown. Filenames that look like a SKU or product name get auto-suggested; the rest you assign manually. Accepted: JPG, PNG, WebP, GIF.
            </div>
            <label className="inline-flex">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={onBulkImgPick}
                data-testid="bulk-images-picker"
              />
              <span className="inline-flex items-center cursor-pointer px-4 py-2 border border-[var(--border)] rounded-md text-sm font-medium hover:bg-[var(--bg-soft)]">
                <FileUp size={14} className="mr-1" /> Choose image files
              </span>
            </label>
            {bulkImgBusy && <p className="text-xs text-gray-500">Working…</p>}
            {bulkImgFiles.length > 0 && (
              <>
                {(() => {
                  const assignedCount = Object.values(bulkImgAssign).filter(v => v && v.id).length;
                  const autoCount = bulkImgMatches.filter(m => m.matched_product_id).length;
                  return (
                    <div className="text-xs text-gray-600 flex gap-4">
                      <span><Check size={12} className="inline text-green-600 mr-1" />
                        {assignedCount} of {bulkImgFiles.length} ready to upload
                      </span>
                      <span className="text-gray-400">({autoCount} auto-suggested by filename)</span>
                    </div>
                  );
                })()}
                <div className="border border-[var(--border)] rounded-md max-h-[55vh] overflow-y-auto">
                  <table className="w-full text-xs" data-testid="bulk-images-matches">
                    <thead className="bg-[var(--bg-soft)] text-gray-500 sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-2 w-16">Preview</th>
                        <th className="text-left p-2 w-40">Filename</th>
                        <th className="text-left p-2">Assign to product</th>
                        <th className="text-left p-2 w-24">Current</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkImgFiles.map((f, i) => {
                        const m = bulkImgMatches.find(x => x.filename === f.name) || {};
                        const picked = bulkImgAssign[f.name];
                        const search = bulkImgSearch[f.name] ?? "";
                        return (
                          <tr key={i} className="border-t border-[var(--border)] align-top">
                            <td className="p-2">
                              <img
                                src={URL.createObjectURL(f)}
                                alt={f.name}
                                className="w-14 h-14 object-cover rounded border border-[var(--border)] bg-[var(--bg-soft)]"
                              />
                            </td>
                            <td className="p-2 font-mono text-[11px] text-gray-700 break-all">{f.name}</td>
                            <td className="p-2">
                              <FreeCombobox
                                value={search}
                                onChange={(v) => {
                                  setBulkImgSearch({ ...bulkImgSearch, [f.name]: v });
                                  if (!v) setBulkImgAssign({ ...bulkImgAssign, [f.name]: null });
                                }}
                                onPick={(p) => {
                                  setBulkImgAssign({ ...bulkImgAssign, [f.name]: { id: p.id, name: p.name, sku: p.sku } });
                                  setBulkImgSearch({ ...bulkImgSearch, [f.name]: p.name });
                                }}
                                options={products}
                                placeholder="Search product by name or SKU…"
                                testId={`bulk-assign-${i}`}
                                getLabel={(p) => p?.name || ""}
                                getSubLabel={(p) => p?.sku || ""}
                              />
                              {picked && (
                                <div className="text-[10px] text-green-700 mt-1 font-mono">
                                  <Check size={10} className="inline" /> {picked.sku}
                                </div>
                              )}
                            </td>
                            <td className="p-2">{m.existing_image_url ? <span className="text-amber-600">will replace</span> : <span className="text-gray-400">none</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setBulkImgOpen(false)} data-testid="bulk-images-cancel">Cancel</Button>
                  <Button
                    className="btn-brand"
                    disabled={bulkImgBusy || Object.values(bulkImgAssign).filter(v => v && v.id).length === 0}
                    onClick={submitBulkImages}
                    data-testid="bulk-images-commit"
                  >
                    Upload {Object.values(bulkImgAssign).filter(v => v && v.id).length} image{Object.values(bulkImgAssign).filter(v => v && v.id).length === 1 ? "" : "s"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate SKUs */}
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="regen-skus-dialog">
          <DialogHeader>
            <DialogTitle>Regenerate SKUs</DialogTitle>
          </DialogHeader>
          <div className="text-sm space-y-4">
            <div className="text-xs text-gray-600 leading-relaxed bg-[var(--bg-soft)] p-3 rounded-md">
              New SKU format: <code className="font-mono text-[var(--brand)]">MP&lt;BRAND&gt;&lt;SIZE&gt;&lt;CATEGORY&gt;-&lt;SUFFIX&gt;</code>
              <br/>Example: a 20 oz Design Essentials Hair Gel → <code className="font-mono">MPDE20HG-A1B2C3</code>
              <br/><strong>BRAND</strong> from vendor name · <strong>SIZE</strong> from product name (oz/ml/lb) · <strong>CATEGORY</strong> from product category · <strong>SUFFIX</strong> = 6 random unambiguous characters (guaranteed unique).
              <br/><span className="text-amber-700">This preview shows the first 200 rows. Committing will regenerate SKUs for all {regenTotal} products in one shot.</span>
            </div>
            {regenBusy && regenRows.length === 0 && <p className="text-xs text-gray-500">Building preview…</p>}
            {regenRows.length > 0 && (
              <div className="border border-[var(--border)] rounded-md max-h-[50vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="regen-skus-preview-table">
                  <thead className="bg-[var(--bg-soft)] text-gray-500 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Product</th>
                      <th className="text-left p-2">Vendor</th>
                      <th className="text-left p-2">Old SKU</th>
                      <th className="text-left p-2">New SKU</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regenRows.map((r) => (
                      <tr key={r.product_id} className="border-t border-[var(--border)]">
                        <td className="p-2 capitalize">{r.name}</td>
                        <td className="p-2 text-gray-500">{r.vendor_name || "—"}</td>
                        <td className="p-2 font-mono text-[10px] text-gray-400">{r.old_sku || "—"}</td>
                        <td className="p-2 font-mono text-[11px] text-[var(--brand)] font-semibold">{r.new_sku}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setRegenOpen(false)} data-testid="regen-skus-cancel">Cancel</Button>
              <Button
                className="btn-brand"
                disabled={regenBusy || regenTotal === 0}
                onClick={submitRegenSkus}
                data-testid="regen-skus-commit"
              >
                Regenerate all {regenTotal} SKUs
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BulkActions section="inventory" records={paged} selectedIds={selectedIds} setSelectedIds={setSelectedIds} onComplete={load} />
    </div>
  );
}
